import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { finalize } from 'rxjs';

import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ButtonModule } from 'primeng/button';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { MarkdownModule } from 'ngx-markdown';

import { BaseComponent } from 'src/app/@shared/components/abstract/base.component';
import { LegalDocument } from 'src/app/@shared/models/legal/legal-document.model';
import { LegalDocumentService } from 'src/app/@core/services/legal/legal-document.service';
import { LegalConsentService } from 'src/app/@core/services/legal/legal-consent.service';
import { LegalStatusEnum } from 'src/app/@shared/enums/legal-status.enum';
import { LegalDocumentTypeEnum } from 'src/app/@shared/enums/legal-document-type.enum';

export type LegalConsentDialogMode = 'blocking' | 'optional' | 'info' | 'no-access';

export interface LegalConsentDialogData {
    documentType: LegalDocumentTypeEnum;
    /** Mode bloquant (KO), optionnel (OBSOLETE), info (depuis menu), ou sans accès (non-admin CGV) */
    mode: LegalConsentDialogMode;
    clientId?: number;
}

@Component({
    selector: 'app-legal-consent-dialog',
    imports: [
        CommonModule,
        ButtonModule,
        ProgressSpinnerModule,
        MarkdownModule,
    ],
    templateUrl: './legal-consent-dialog.component.html',
    styleUrl: './legal-consent-dialog.component.scss'
})
export class LegalConsentDialogComponent extends BaseComponent implements OnInit {

    private _config = inject(DynamicDialogConfig);
    private _ref = inject(DynamicDialogRef);
    private _legalDocumentService = inject(LegalDocumentService);
    private _legalConsentService = inject(LegalConsentService);

    /*----------------------------------------------------------------------------------------
     * Données
     ----------------------------------------------------------------------------------------*/

    protected override _loading = true;
    public document: LegalDocument | null = null;
    public accepting = false;

    /*----------------------------------------------------------------------------------------
     * Accesseurs
     ----------------------------------------------------------------------------------------*/

    public get acceptLabel(): string {
        if (this.data.documentType === LegalDocumentTypeEnum.PRIVACY) return 'J\'ai pris connaissance';
        return 'J\'accepte';
    }

    public get data(): LegalConsentDialogData {
        return this._config.data;
    }

    public get mode(): LegalConsentDialogMode {
        return this.data.mode;
    }

    public get isBlocking(): boolean {
        return this.mode === 'blocking';
    }

    public get isOptional(): boolean {
        return this.mode === 'optional';
    }

    public get isInfo(): boolean {
        return this.mode === 'info';
    }

    public get isNoAccess(): boolean {
        return this.mode === 'no-access';
    }

    public get canAccept(): boolean {
        return (this.isBlocking || this.isOptional || this.isInfo) && !!this.document;
    }

    public get documentTypeLabel(): string {
        switch (this.data.documentType) {
            case LegalDocumentTypeEnum.CGU: return 'Conditions Générales d\'Utilisation';
            case LegalDocumentTypeEnum.PRIVACY: return 'Politique de Confidentialité';
            case LegalDocumentTypeEnum.CGV: return 'Conditions Générales de Vente';
        }
    }

    /*----------------------------------------------------------------------------------------
     * Lifecycle
     ----------------------------------------------------------------------------------------*/

    constructor() {
        super();
        this._config.header = this.documentTypeLabel;
    }

    override ngOnInit(): void {
        super.ngOnInit();

        if (!this.isNoAccess) {
            this._loadDocument();
        } else {
            this.loading = false;
        }
    }

    /*----------------------------------------------------------------------------------------
     * Méthodes publiques
     ----------------------------------------------------------------------------------------*/

    public accept(): void {
        if (!this.document) return;
        this.accepting = true;
        this._legalConsentService.accept$(this.document.id, this.data.clientId)
            .pipe(finalize(() => this.accepting = false))
            .subscribe(consent => {
                if (consent) {
                    this._ref.close({ accepted: true, documentType: this.data.documentType });
                }
            });
    }

    public dismiss(): void {
        this._ref.close({ accepted: false });
    }

    /*----------------------------------------------------------------------------------------
     * Méthodes privées
     ----------------------------------------------------------------------------------------*/

    private _loadDocument(): void {
        this.loading = true;
        this._legalDocumentService.getLatest$(this.data.documentType)
            .pipe(finalize(() => this.loading = false))
            .subscribe(document => {
                this.document = document;
            });
    }
}
